import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Sparkles,
  Calendar,
  CreditCard,
  Bell,
  CheckCircle,
  MessageCircle,
  CalendarPlus,
  Clock,
  User,
  FileText,
  ArrowRight,
  ChevronDown,
  Home,
  Book,
  Store,
  CircleDollarSign,
  Users,
  HelpCircle,
  TrendingUp,
  Plus,
  Search,
  Dog,
} from 'lucide-react';
import '../../pages/LandingPageV2.css';

/* Mock carte RDV façon Rintintin (style Accueil / planning du jour) */
const MockScheduleCard = ({
  time,
  clientName,
  serviceName,
  animalName,
  status = 'confirmed',
}: { time: string; clientName: string; serviceName: string; animalName?: string; status?: 'confirmed' | 'pending' }) => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      padding: '14px 16px',
      borderRadius: '14px',
      backgroundColor: status === 'confirmed' ? 'linear-gradient(135deg, #f0fdf4 0%, #f8fafc 100%)' : '#fff7ed',
      background: status === 'confirmed' ? 'linear-gradient(135deg, #f0fdf4 0%, #f8fafc 100%)' : 'linear-gradient(135deg, #fff7ed 0%, #f8fafc 100%)',
      borderLeft: `4px solid ${status === 'confirmed' ? '#10b981' : '#f97316'}`,
      gap: '12px',
      marginBottom: '10px',
    }}
  >
    <div style={{ width: '80px', fontWeight: 700, color: '#334155', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
      <Clock size={14} color="#94a3b8" />
      {time}
    </div>
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ fontWeight: 700, color: '#1e293b', fontSize: '14px', marginBottom: '2px' }}>{clientName}</div>
      <div style={{ fontSize: '13px', color: '#64748b' }}>{serviceName}</div>
      {animalName && <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '2px' }}>{animalName}</div>}
    </div>
    {status === 'confirmed' && <CheckCircle size={18} color="#10b981" />}
    {status === 'pending' && <Bell size={16} color="#f97316" />}
  </div>
);

/* Widgets type tableau de bord affichés derrière l’animation des notifications (style référence) */
const SHOWCASE_TAB_DURATION_MS = 4500;
/** Indices sidebar pour le cycle : Accueil=0, Planning=1, Réservations=2, Clients=5 (pas Offres/Business/Documents) */
export const SHOWCASE_SIDEBAR_INDICES = [0, 1, 2, 5];
/** Mobile / tablette : on saute Planning (grille illisible) et Réservations. */
export const SHOWCASE_SIDEBAR_INDICES_COMPACT = [0, 5];

function useCompactShowcase() {
  const [on, setOn] = useState(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return false;
    }
    return window.matchMedia('(max-width: 992px)')?.matches === true;
  });
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return undefined;
    }
    const mq = window.matchMedia('(max-width: 992px)');
    if (!mq) return undefined;
    const update = () => setOn(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);
  return on;
}

const AppShowcaseVisual = () => {
  const { t } = useTranslation('marketing');
  const ns = 'appShowcaseVisual';
  const compactShowcase = useCompactShowcase();
  const cycleIndices = compactShowcase ? SHOWCASE_SIDEBAR_INDICES_COMPACT : SHOWCASE_SIDEBAR_INDICES;
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const sidebarIndex = cycleIndices[activeTabIndex % cycleIndices.length] ?? 0;

  const sidebarMenuItems = useMemo(
    () => [
      { label: t(`${ns}.sidebar.home`), icon: Home as React.ComponentType<Record<string, unknown>> },
      { label: t(`${ns}.sidebar.planning`), icon: Calendar as React.ComponentType<Record<string, unknown>> },
      { label: t(`${ns}.sidebar.reservations`), icon: Book as React.ComponentType<Record<string, unknown>> },
      { label: t(`${ns}.sidebar.offers`), icon: Store as React.ComponentType<Record<string, unknown>>, hasArrow: true },
      { label: t(`${ns}.sidebar.business`), icon: CircleDollarSign as React.ComponentType<Record<string, unknown>>, hasArrow: true },
      { label: t(`${ns}.sidebar.clients`), icon: Users as React.ComponentType<Record<string, unknown>> },
      { label: t(`${ns}.sidebar.documents`), icon: FileText as React.ComponentType<Record<string, unknown>> },
    ],
    [t]
  );

  const tabCount = cycleIndices.length;

  type ClientSample = {
    name: string;
    count: number;
    borderColor: string;
    animals: Array<{ name: string; breed: string }>;
  };

  const clientSamples = useMemo(
    () => t(`${ns}.clients.samples`, { returnObjects: true }) as ClientSample[],
    [t]
  );

  const homeScheduleItems = useMemo(
    () => [
      { time: '08:00', client: 'Thomas F.', service: t(`${ns}.services.groupClass`), animal: 'Max', statusColor: '#10b981', bg: '#f0fbf7' },
      { time: '09:00', client: 'Alice Bernard', service: t(`${ns}.services.basicTraining`), animal: 'Rex', statusColor: '#10b981', bg: '#f0fbf7' },
      { time: '10:30', client: 'Julie M.', service: t(`${ns}.services.grooming`), animal: 'Nala', statusColor: '#10b981', bg: '#f0fbf7' },
      { type: 'unavailability' as const, time: '11:30 - 13:00', label: t(`${ns}.home.unavailability`), reason: t(`${ns}.home.lunchBreak`), bg: '#f1f5f9' },
      { time: '13:30', client: 'Pierre D.', service: t(`${ns}.services.homeVisit`), animal: 'Médor', statusColor: '#f97316', bg: '#fff9f0' },
      { time: '14:30', client: 'Jean Durand', service: t(`${ns}.services.boarding`), animal: 'Luna', statusColor: '#10b981', bg: '#f0fbf7' },
      { time: '16:00', client: 'Marie Loft', service: t(`${ns}.services.grooming`), animal: 'Bella', statusColor: '#10b981', bg: '#f0fbf7' },
      { time: '17:30', client: 'Sophie L.', service: t(`${ns}.services.training`), animal: 'Rex', statusColor: '#f97316', bg: '#fff9f0' },
    ],
    [t]
  );

  const statsItems = useMemo(
    () => [
      { label: t(`${ns}.stats.revenue`), val: '12 840€', sub: t(`${ns}.stats.thisMonth`), icon: TrendingUp, color: '#10b981', bg: '#f0fdf4' },
      { label: t(`${ns}.stats.bookings`), val: '187', sub: t(`${ns}.stats.pendingCount`), icon: Calendar, color: '#3b82f6', bg: '#eff6ff' },
      { label: t(`${ns}.stats.newClients`), val: '23', sub: t(`${ns}.stats.thisMonth`), icon: User, color: '#f97316', bg: '#fff7ed' },
    ],
    [t]
  );

  const planningViewLabels = useMemo(
    () => [
      t(`${ns}.planning.views.month`),
      t(`${ns}.planning.views.week`),
      t(`${ns}.planning.views.day`),
      t(`${ns}.planning.views.agenda`),
    ],
    [t]
  );

  const planningEvents = useMemo(
    () => [
      { h: 8, day: 0, title: t(`${ns}.services.groupClass`), client: 'Thomas F.', color: 'linear-gradient(135deg, #7FB069, #9ACDAB)' },
      { h: 10, day: 0, title: t(`${ns}.services.homeVisit`), client: 'Sophie L.', color: 'rgba(165,118,92,0.25)' },
      { h: 14, day: 0, title: t(`${ns}.services.boarding`), client: 'Pierre D.', color: 'linear-gradient(135deg, #A5765C, #F6C674)' },
      { h: 9, day: 1, title: t(`${ns}.services.training`), client: 'Paul M.', color: 'linear-gradient(135deg, #7FB069, #9ACDAB)' },
      { h: 11, day: 1, title: t(`${ns}.services.grooming`), client: 'Julie M.', color: 'linear-gradient(135deg, #77A6B6, #9ACDAB)' },
      { h: 15, day: 1, title: t(`${ns}.services.boarding`), client: 'Camille R.', color: 'linear-gradient(135deg, #A5765C, #F6C674)' },
      { h: 9, day: 2, title: t(`${ns}.services.basicTraining`), client: 'Alice Bernard', color: 'linear-gradient(135deg, #7FB069, #9ACDAB)' },
      { h: 11, day: 2, title: t(`${ns}.planning.break`), client: '', color: 'rgba(148,163,184,0.25)' },
      { h: 14, day: 2, title: t(`${ns}.services.boarding`), client: 'Jean Durand', color: 'linear-gradient(135deg, #A5765C, #F6C674)' },
      { h: 16, day: 2, title: t(`${ns}.services.grooming`), client: 'Marie Loft', color: 'linear-gradient(135deg, #77A6B6, #9ACDAB)' },
      { h: 8, day: 3, title: t(`${ns}.services.visit`), client: 'Thomas B.', color: 'rgba(165,118,92,0.2)' },
      { h: 10, day: 3, title: t(`${ns}.services.training`), client: 'Sophie L.', color: 'linear-gradient(135deg, #7FB069, #9ACDAB)' },
      { h: 14, day: 3, title: t(`${ns}.services.grooming`), client: 'Marie Loft', color: 'linear-gradient(135deg, #77A6B6, #9ACDAB)' },
      { h: 17, day: 3, title: t(`${ns}.services.groupClass`), client: 'Julie M.', color: 'linear-gradient(135deg, #7FB069, #9ACDAB)' },
      { h: 9, day: 4, title: t(`${ns}.services.boarding`), client: 'Jean Durand', color: 'linear-gradient(135deg, #A5765C, #F6C674)' },
      { h: 11, day: 4, title: t(`${ns}.services.training`), client: 'Alice Bernard', color: 'linear-gradient(135deg, #7FB069, #9ACDAB)' },
      { h: 16, day: 4, title: t(`${ns}.services.grooming`), client: 'Paul M.', color: 'linear-gradient(135deg, #77A6B6, #9ACDAB)' },
      { h: 10, day: 5, title: t(`${ns}.services.homeVisit`), client: 'Pierre D.', color: 'rgba(165,118,92,0.2)' },
      { h: 14, day: 5, title: t(`${ns}.services.boarding`), client: 'Camille R.', color: 'linear-gradient(135deg, #A5765C, #F6C674)' },
    ],
    [t]
  );

  const reservationBookings = useMemo(
    () => {
      const samples = t(`${ns}.reservations.samples`, { returnObjects: true }) as Array<{ date: string; time: string }>;
      return [
      { service: t(`${ns}.services.homeVisit`), date: samples[0]?.date ?? '', time: samples[0]?.time ?? '', client: 'Sophie L.', animal: 'Rex', status: t(`${ns}.reservations.filters.clientRequest`), statusColor: '#f97316', statusBg: '#fff7ed', gradient: 'linear-gradient(to right, #F6C674, #A5765C)' },
      { service: t(`${ns}.services.basicTraining`), date: samples[1]?.date ?? '', time: samples[1]?.time ?? '', client: 'Paul M.', animal: 'Luna', status: t(`${ns}.reservations.filters.clientRequest`), statusColor: '#f97316', statusBg: '#fff7ed', gradient: 'linear-gradient(to right, #F6C674, #A5765C)' },
      { service: t(`${ns}.services.boardingStay`), date: samples[2]?.date ?? '', time: samples[2]?.time ?? '', client: 'Thomas B.', animal: 'Max & Nala', status: t(`${ns}.reservations.filters.clientRequest`), statusColor: '#f97316', statusBg: '#fff7ed', gradient: 'linear-gradient(to right, #4facfe, #00f2fe)' },
      { service: t(`${ns}.services.grooming`), date: samples[3]?.date ?? '', time: samples[3]?.time ?? '', client: 'Julie M.', animal: 'Nala', status: t(`${ns}.reservations.filters.validated`), statusColor: '#10b981', statusBg: '#f0fdf4', gradient: 'linear-gradient(to right, #F6C674, #A5765C)' },
      { service: t(`${ns}.services.groupClass`), date: samples[4]?.date ?? '', time: samples[4]?.time ?? '', client: 'Camille R.', animal: 'Rex', status: t(`${ns}.reservations.filters.clientRequest`), statusColor: '#f97316', statusBg: '#fff7ed', gradient: 'linear-gradient(to right, #F6C674, #A5765C)' },
      { service: t(`${ns}.services.boarding`), date: samples[5]?.date ?? '', time: samples[5]?.time ?? '', client: 'Jean Durand', animal: 'Luna', status: t(`${ns}.reservations.filters.validated`), statusColor: '#10b981', statusBg: '#f0fdf4', gradient: 'linear-gradient(to right, #F6C674, #A5765C)' },
    ];
    },
    [t]
  );

  const pendingReservations = useMemo(
    () => [
      { name: 'Sophie L.', detail: `${t(`${ns}.services.homeVisit`)} • 22/03` },
      { name: 'Paul M.', detail: `${t(`${ns}.services.training`)} • 25/03` },
      { name: 'Thomas B.', detail: `${t(`${ns}.services.boarding`)} • 24/03 - 26/03` },
      { name: 'Julie M.', detail: `${t(`${ns}.services.grooming`)} • 23/03` },
      { name: 'Camille R.', detail: `${t(`${ns}.services.groupClass`)} • 26/03` },
    ],
    [t]
  );

  useEffect(() => {
    setActiveTabIndex((prev) => (prev >= tabCount ? 0 : prev));
    const timer = setInterval(() => {
      setActiveTabIndex((prev) => (prev + 1) % tabCount);
    }, SHOWCASE_TAB_DURATION_MS);
    return () => clearInterval(timer);
  }, [tabCount]);

  return (
    <div className="lp-hero__showcase" style={{ perspective: '1500px' }}>
      {/* Simulation de l'interface V4 "Peps Premium" */}
      <motion.div 
        className="lp-hero__app-window"
        initial={{ opacity: 0, scale: 0.95, rotateY: -15, rotateX: 5 }}
        animate={{ opacity: 1, scale: 1, rotateY: -15, rotateX: 5 }}
        transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
        style={{ 
          background: '#f8f9fb', 
          border: 'none', 
          boxShadow: '0 40px 100px rgba(0,0,0,0.18)',
          padding: '12px',
          display: 'flex',
          gap: '12px'
        }}
      >
        {/* SIDEBAR ISLAND */}
        <div
          className="lp-hero__showcase-sidebar"
          style={{ 
          width: '210px', // Un peu réduit pour laisser de la place
          background: '#fff', 
          borderRadius: '24px', 
          boxShadow: '0 4px 25px rgba(0,0,0,0.06)',
          display: 'flex',
          flexDirection: 'column',
          flexShrink: 0,
          border: '1px solid rgba(0,0,0,0.02)',
          paddingBottom: '16px'
        }}>
          {/* Logo Section */}
          <div style={{ padding: '24px 20px', textAlign: 'left' }}>
             <div style={{ fontSize: '1.5rem', fontWeight: 900, color: '#1e293b', letterSpacing: '-0.7px' }}>
               Rintintin<span style={{ color: '#D4A373' }}>Pro</span><span style={{ color: '#D4A373' }}>.</span>
             </div>
          </div>
          
          {/* Menu Items — surligner Accueil, Planning, Réservations, Clients (pas Offres) */}
          <div style={{ padding: '0 8px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {sidebarMenuItems.map((item, i) => {
              const isActive = sidebarIndex === i;
              return (
                <div
                  key={item.label}
                  style={{ 
                    padding: '10px 12px', 
                    borderRadius: '12px', 
                    background: isActive ? '#B08968' : 'transparent',
                    color: isActive ? '#fff' : '#334155',
                    fontSize: '13px',
                    fontWeight: isActive ? 700 : 600,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    position: 'relative',
                    overflow: 'hidden',
                    transition: 'background 0.35s ease, color 0.35s ease'
                  }}
                >
                  {isActive && <div style={{ width: '5px', height: '100%', background: '#F6C674', position: 'absolute', left: 0, top: 0, borderRadius: '12px 0 0 12px' }} />}
                  <item.icon size={18} strokeWidth={isActive ? 2.5 : 2} color={isActive ? '#fff' : '#A5765C'} />
                  <span style={{ flex: 1 }}>{item.label}</span>
                  {item.hasArrow && !isActive && <ChevronDown size={14} color="#A5765C" strokeWidth={3} />}
                </div>
              );
            })}
          </div>

          <div style={{ marginTop: 'auto', padding: '0 8px' }}>
            {/* Help Center Link */}
            <div style={{ 
              padding: '10px 12px', 
              display: 'flex', 
              alignItems: 'center', 
              gap: '10px', 
              color: '#64748b', 
              fontSize: '13px', 
              fontWeight: 600 
            }}>
               <HelpCircle size={18} color="#64748b" strokeWidth={2} /> {t(`${ns}.sidebar.helpCenter`)}
            </div>
          </div>
        </div>

        {/* MAIN CONTENT AREA */}
        <div className="lp-hero__showcase-main" style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '12px' }}>
          
          {/* HEADER ISLAND — rangée pleine largeur, pill ancrée à droite (évite le “flottement” si la zone contenu se compacte) */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', flexShrink: 0 }}>
            <div style={{ 
              height: '52px', 
              background: 'white', 
              borderRadius: '32px', 
              boxShadow: '0 12px 40px rgba(0,0,0,0.12)',
              display: 'flex',
              alignItems: 'center',
              padding: '4px 8px 4px 16px',
              gap: '12px',
              border: '1px solid rgba(0,0,0,0.03)'
            }}>
               <Sparkles size={18} color="#A5765C" />
               <div style={{ width: '1px', height: '20px', background: 'rgba(0,0,0,0.05)' }} />
               <div style={{ position: 'relative' }}>
                  <MessageCircle size={18} color="#666" />
                  <div style={{ position: 'absolute', top: -4, right: -4, width: '14px', height: '14px', background: '#ff4d4f', borderRadius: '50%', border: '2px solid #fff', fontSize: '8px', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900 }}>3</div>
               </div>
               <Bell size={18} color="#666" />
               <div style={{ width: '1px', height: '24px', background: 'rgba(0,0,0,0.05)' }} />
               <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '4px 8px', background: 'rgba(165,118,92,0.05)', borderRadius: '20px' }}>
                 <img 
                   src="https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?auto=format&fit=crop&q=80&w=100&h=100" 
                   style={{ width: '28px', height: '28px', borderRadius: '50%', border: '2px solid #F6C674', objectFit: 'cover' }} 
                   alt={t(`${ns}.header.profileAlt`)}
                 />
                 <span style={{ fontSize: '12px', fontWeight: 700, color: '#A5765C' }}>{t(`${ns}.header.profileName`)}</span>
                 <ArrowRight size={12} color="#A5765C" style={{ transform: 'rotate(90deg)' }} />
               </div>
            </div>
          </div>

          {/* DASHBOARD CONTENT CONTAINER — rotation auto Accueil / Planning / Réservations / Clients */}
          <div style={{ flex: 1, minHeight: 0, overflow: 'hidden', position: 'relative' }}>
            <AnimatePresence mode="wait" initial={false}>
              <motion.div
                key={sidebarIndex}
                initial={{ opacity: 0, x: 12 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -12 }}
                transition={{ duration: 0.35, ease: [0.25, 0.46, 0.45, 0.94] }}
                style={{ position: 'absolute', inset: 0, overflowY: 'auto' }}
              >
            {sidebarIndex === 0 && (
            <>
            {/* VUE ACCUEIL */}
            <div style={{ 
              background: '#B08968', 
              borderRadius: '24px', 
              padding: '20px 24px', 
              marginBottom: '16px',
              color: '#fff',
              position: 'relative',
              overflow: 'hidden',
              boxShadow: '0 10px 30px rgba(176,137,104,0.2)'
            }}>
              <div style={{ position: 'relative', zIndex: 1 }}>
                <div style={{ margin: 0, fontSize: '20px', fontWeight: 900 }}>{t(`${ns}.home.greeting`)}</div>
                <div style={{ margin: '4px 0 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Calendar size={14} style={{ opacity: 0.8 }} />
                  <span style={{ fontSize: '12px', opacity: 0.9, fontWeight: 500 }}>{t(`${ns}.home.todayDate`)}</span>
                </div>
              </div>
              {/* Decorative shapes */}
              <div style={{ position: 'absolute', right: '-40px', bottom: '-40px', width: '120px', height: '120px', background: 'rgba(255,255,255,0.1)', borderRadius: '50%' }} />
              <div style={{ position: 'absolute', right: '40px', top: '-20px', width: '70px', height: '70px', background: 'rgba(255,255,255,0.05)', borderRadius: '50%' }} />
            </div>

            {/* DASHBOARD GRID SYSTEM */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 220px', gap: '16px' }}>
              
              {/* LEFT COLUMN: Planning du jour */}
              <div style={{ background: '#fff', borderRadius: '24px', padding: '20px', boxShadow: '0 4px 20px rgba(0,0,0,0.03)', border: '1px solid rgba(0,0,0,0.01)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '18px' }}>
                  <div style={{ margin: 0, fontSize: '15px', fontWeight: 900, color: '#1e293b' }}>{t(`${ns}.home.todayPlanning`)}</div>
                  <div style={{ fontSize: '11px', color: '#A5765C', fontWeight: 800, cursor: 'pointer', letterSpacing: '0.5px' }}>{t(`${ns}.home.seeAll`)}</div>
                </div>
                
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  {homeScheduleItems.map((item, i) => (
                    <div key={i} style={{ 
                      padding: '10px 14px', 
                      borderRadius: '16px', 
                      background: item.bg,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      border: '1px solid rgba(0,0,0,0.02)'
                    }}>
                      <div style={{ fontSize: '11px', fontWeight: 900, color: '#1e293b', width: '40px' }}>{item.time}</div>
                      <div style={{ flex: 1 }}>
                        {item.type === 'unavailability' ? (
                          <div style={{ fontSize: '12px', fontWeight: 700, color: '#475569' }}>{item.reason}</div>
                        ) : (
                          <>
                            <div style={{ fontSize: '12px', fontWeight: 800, color: '#A5765C' }}>{item.client}</div>
                            <div style={{ fontSize: '10px', color: '#64748b' }}>{item.service} • <span style={{ fontWeight: 700, color: '#000' }}>{item.animal}</span></div>
                          </>
                        )}
                      </div>
                      {item.statusColor && (
                        <div style={{ color: item.statusColor }}>
                          {item.statusColor === '#10b981' ? <CheckCircle size={18} strokeWidth={2.5} /> : <Clock size={18} strokeWidth={2.5} />}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* RIGHT COLUMN: Actions + Pending */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                
                {/* Quick Actions Card */}
                <div style={{ background: '#fff', borderRadius: '24px', padding: '16px', boxShadow: '0 4px 20px rgba(0,0,0,0.03)', border: '1px solid rgba(0,0,0,0.01)' }}>
                  <div style={{ margin: '0 0 12px 0', fontSize: '13px', fontWeight: 900, color: '#1e293b' }}>{t(`${ns}.home.quickActions`)}</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    <div style={{ 
                      background: '#B08968', 
                      color: '#fff', 
                      borderRadius: '14px', 
                      padding: '12px', 
                      textAlign: 'center',
                      boxShadow: '0 8px 15px rgba(176,137,104,0.25)',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px'
                    }}>
                      <CalendarPlus size={16} strokeWidth={2.5} />
                      <span style={{ fontSize: '13px', fontWeight: 800 }}>{t(`${ns}.home.schedule`)}</span>
                    </div>
                    <div style={{ 
                      background: 'rgba(165,118,92,0.06)', 
                      border: '1px solid rgba(165,118,92,0.2)', 
                      borderRadius: '14px', 
                      padding: '11px', 
                      textAlign: 'center',
                      fontSize: '12.5px',
                      fontWeight: 700,
                      color: '#A5765C'
                    }}>
                      {t(`${ns}.home.myCalendar`)}
                    </div>
                  </div>
                </div>

                {/* Pending Reservations Mini-List */}
                <div style={{ background: '#fff', borderRadius: '24px', padding: '16px', boxShadow: '0 4px 20px rgba(0,0,0,0.03)', border: '1px solid rgba(0,0,0,0.01)', flex: 1, maxHeight: '200px', overflowY: 'auto' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                    <div style={{ margin: 0, fontSize: '13px', fontWeight: 900, color: '#1e293b' }}>{t(`${ns}.home.pending`)}</div>
                    <div style={{ background: '#f97316', color: '#fff', fontSize: '10px', fontWeight: 900, minWidth: '18px', height: '18px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 4px' }}>5</div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    {pendingReservations.map((r, i) => (
                      <div key={i} style={{ padding: '8px 10px', borderRadius: '12px', background: '#fffaf5', border: '1px solid #ffedd5' }}>
                        <div style={{ fontSize: '11px', fontWeight: 800, color: '#1e293b' }}>{r.name}</div>
                        <div style={{ fontSize: '10px', color: '#9a3412' }}>{r.detail}</div>
                      </div>
                    ))}
                  </div>
                </div>

              </div>

            </div>

            {/* FULL WIDTH STATS ROW */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '14px', marginTop: '16px' }}>
              {statsItems.map((stat, i) => (
                <div key={stat.label} style={{ 
                  borderRadius: '22px', 
                  padding: '16px', 
                  borderLeft: `5px solid ${stat.color}`,
                  boxShadow: '0 4px 15px rgba(0,0,0,0.03)',
                  background: `linear-gradient(135deg, #fff 0%, ${stat.bg} 100%)`,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '4px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <stat.icon size={13} color={stat.color} />
                    <span style={{ fontSize: '10px', fontWeight: 900, color: '#64748b', letterSpacing: '0.5px' }}>{stat.label}</span>
                  </div>
                  <div style={{ fontSize: '20px', fontWeight: 900, color: '#1e293b', marginTop: '2px' }}>{stat.val}</div>
                  {'sub' in stat && stat.sub && <div style={{ fontSize: '9px', color: '#94a3b8', marginTop: '2px' }}>{stat.sub}</div>}
                </div>
              ))}
            </div>
            </>
            )}

            {sidebarIndex === 1 && (
              <div style={{ padding: '4px 0' }}>
                {/* Barre type CustomToolbar : ‹ Semaine du … › */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px', flexWrap: 'wrap', gap: '8px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ padding: '6px 12px', borderRadius: '10px', background: '#f1f5f9', fontSize: '13px', fontWeight: 700, color: '#334155', cursor: 'pointer' }}>‹</span>
                    <span style={{ fontSize: '15px', fontWeight: 800, color: '#1e293b' }}>{t(`${ns}.planning.weekOf`)}</span>
                    <span style={{ padding: '6px 12px', borderRadius: '10px', background: '#f1f5f9', fontSize: '13px', fontWeight: 700, color: '#334155', cursor: 'pointer' }}>›</span>
                  </div>
                  <div style={{ display: 'flex', gap: '6px' }}>
                    {planningViewLabels.map((v, i) => (
                      <span key={v} style={{ padding: '6px 10px', borderRadius: '8px', fontSize: '12px', fontWeight: 600, background: i === 1 ? '#B08968' : '#f8fafc', color: i === 1 ? '#fff' : '#64748b' }}>{v}</span>
                    ))}
                  </div>
                </div>
                {/* Calendrier semaine : créneaux horaires + colonnes jours + événements */}
                <div style={{ background: '#fff', borderRadius: '16px', boxShadow: '0 4px 20px rgba(0,0,0,0.06)', border: '1px solid rgba(0,0,0,0.04)', overflow: 'hidden' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '44px repeat(7, 1fr)', fontSize: '11px', fontWeight: 700, color: '#64748b', borderBottom: '1px solid #e2e8f0' }}>
                    <div style={{ padding: '8px 4px', borderRight: '1px solid #e2e8f0' }} />
                    {['Lun 16', 'Mar 17', 'Mer 18', 'Jeu 19', 'Ven 20', 'Sam 21', 'Dim 22'].map((d, i) => (
                      <div key={d} style={{ padding: '8px 4px', textAlign: 'center', borderRight: i < 6 ? '1px solid #e2e8f0' : 'none' }}>{d}</div>
                    ))}
                  </div>
                  {[8, 9, 10, 11, 12, 13, 14, 15, 16, 17].map((hour) => (
                    <div key={hour} style={{ display: 'grid', gridTemplateColumns: '44px repeat(7, 1fr)', minHeight: '26px', borderBottom: '1px solid #f1f5f9' }}>
                      <div style={{ padding: '4px 4px', borderRight: '1px solid #e2e8f0', color: '#94a3b8', fontSize: '10px' }}>{hour}h00</div>
                      {[0,1,2,3,4,5,6].map((day) => {
                        const ev = planningEvents.find(e => e.h === hour && e.day === day);
                        return (
                          <div key={day} style={{ borderRight: day < 6 ? '1px solid #f1f5f9' : 'none', position: 'relative', minHeight: '28px' }}>
                            {ev && (
                              <div style={{ position: 'absolute', inset: '2px', borderRadius: '8px', background: ev.color, padding: '4px 6px', overflow: 'hidden', fontSize: '10px', fontWeight: 700, color: ev.client ? '#1e293b' : '#64748b' }}>
                                {ev.client ? (
                                  <>
                                    <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{ev.client}</div>
                                    <div style={{ fontSize: '9px', opacity: 0.9 }}>{ev.title}</div>
                                  </>
                                ) : ev.title}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {sidebarIndex === 2 && (
              <div style={{ padding: '4px 0' }}>
                {/* En-tête : bouton Programmer (style planning-tab) */}
                <div style={{ marginBottom: '14px', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 16px', borderRadius: '12px', background: '#B08968', color: '#fff', fontWeight: 700, fontSize: '13px', boxShadow: '0 4px 12px rgba(176,137,104,0.3)' }}>
                    <CalendarPlus size={16} strokeWidth={2.5} />
                    <span>{t(`${ns}.reservations.schedule`)}</span>
                    <ChevronDown size={14} />
                  </div>
                </div>
                {/* Filtres type ReservationsBookingFilters */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '14px', padding: '10px 14px', background: '#fff', borderRadius: '14px', boxShadow: '0 2px 10px rgba(0,0,0,0.04)', border: '1px solid rgba(0,0,0,0.04)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 12px', borderRadius: '20px', background: '#fef3c7', color: '#92400e', fontWeight: 700, fontSize: '12px' }}>
                    <User size={14} /> {t(`${ns}.reservations.filters.clientRequest`)} <span style={{ background: '#f97316', color: '#fff', padding: '0 6px', borderRadius: '999px', marginLeft: '4px' }}>4</span>
                  </div>
                  <div style={{ padding: '6px 12px', borderRadius: '20px', background: '#f8fafc', color: '#64748b', fontWeight: 600, fontSize: '12px' }}>{t(`${ns}.reservations.filters.pendingClient`)}</div>
                  <div style={{ padding: '6px 12px', borderRadius: '20px', background: '#f8fafc', color: '#64748b', fontWeight: 600, fontSize: '12px' }}>{t(`${ns}.reservations.filters.validated`)}</div>
                  <div style={{ padding: '6px 12px', borderRadius: '20px', background: '#f8fafc', color: '#64748b', fontWeight: 600, fontSize: '12px' }}>{t(`${ns}.reservations.filters.declined`)}</div>
                  <div style={{ padding: '6px 12px', borderRadius: '20px', background: '#f8fafc', color: '#64748b', fontWeight: 600, fontSize: '12px' }}>{t(`${ns}.reservations.filters.past`)}</div>
                </div>
                {/* Liste type ReservationsBookingsList : cartes avec barre dégradée + service, date, client, chip */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', maxHeight: '320px', overflowY: 'auto' }}>
                  {reservationBookings.map((b, i) => (
                    <div key={i} style={{ position: 'relative', overflow: 'hidden', borderRadius: '16px', background: '#fff', border: '1px solid rgba(165,118,92,0.12)', boxShadow: '0 4px 20px rgba(165,118,92,0.08)', flexShrink: 0 }}>
                      <div style={{ height: '4px', background: b.gradient, borderRadius: '16px 16px 0 0' }} />
                      <div style={{ padding: '14px 16px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
                          <span style={{ fontWeight: 800, color: '#A5765C', fontSize: '14px' }}>{b.service}</span>
                          <span style={{ fontSize: '11px', fontWeight: 700, padding: '3px 8px', borderRadius: '999px', background: b.statusBg, color: b.statusColor }}>{b.status}</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', padding: '10px 12px', background: 'rgba(165,118,92,0.04)', borderRadius: '12px', border: '1px solid rgba(165,118,92,0.1)' }}>
                          <span style={{ fontSize: '12px', fontWeight: 600, color: '#A5765C' }}>📅 {b.date} · {b.time}</span>
                        </div>
                        <div style={{ marginTop: '10px', fontSize: '13px', fontWeight: 700, color: '#1e293b' }}>{b.client} · {b.animal}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {sidebarIndex === 5 && (
              <div style={{ padding: '4px 0' }}>
                {/* En-tête type ClientsChiensPage : titre + badge count + bouton Nouveau client */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px', marginBottom: '14px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{ margin: 0, fontSize: '18px', fontWeight: 900, color: '#1e293b' }}>{t(`${ns}.clients.title`)}</div>
                    <span style={{ fontSize: '13px', color: '#666', fontWeight: 500, background: '#f5f5f5', padding: '4px 12px', borderRadius: '12px' }}>{t(`${ns}.clients.count`, { count: 152 })}</span>
                  </div>
                  <div style={{ padding: '8px 14px', borderRadius: '12px', background: '#B08968', color: '#fff', fontWeight: 700, fontSize: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <Plus size={14} /> {t(`${ns}.clients.newClient`)}
                  </div>
                </div>
                {/* Recherche type search-section */}
                <div style={{ marginBottom: '14px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 14px', background: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
                    <Search size={16} color="#94a3b8" />
                    <span style={{ flex: 1, fontSize: '13px', color: '#64748b' }}>{t(`${ns}.clients.searchPlaceholder`)}</span>
                  </div>
                </div>
                {/* Grille type clients-grid / client-card-modern */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px', maxHeight: '280px', overflowY: 'auto' }}>
                  {clientSamples.map((c, i) => (
                    <div key={i} style={{ background: '#fff', borderRadius: '16px', padding: '12px', boxShadow: '0 4px 16px rgba(0,0,0,0.05)', border: '1px solid rgba(0,0,0,0.04)', flexShrink: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                        <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: 'linear-gradient(135deg, #B08968, #D4A373)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 800, fontSize: '12px', flexShrink: 0, border: `3px solid ${c.borderColor}` }}>
                          {c.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ margin: 0, fontSize: '13px', fontWeight: 800, color: '#1e293b' }}>{c.name}</div>
                          <p style={{ margin: '2px 0 6px', fontSize: '11px', color: '#A5765C', fontWeight: 600 }}>{c.count} {t(`${ns}.clients.dog`, { count: c.count })}</p>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                            {c.animals.slice(0, 3).map((a, j) => (
                              <span key={j} style={{ padding: '3px 6px', borderRadius: '6px', background: 'rgba(165,118,92,0.08)', fontSize: '10px', fontWeight: 600, color: '#334155' }}>
                                <strong>{a.name}</strong> · {a.breed}
                              </span>
                            ))}
                            {c.animals.length > 3 && <span style={{ fontSize: '10px', color: '#64748b', fontWeight: 600 }}>+{c.animals.length - 3}</span>}
                          </div>
                        </div>
                        <ArrowRight size={12} color="#94a3b8" style={{ flexShrink: 0, marginTop: '2px' }} />
                      </div>
                    </div>
                  ))}
                </div>
                {/* Pagination type clients */}
                <div style={{ display: 'flex', justifyContent: 'center', gap: '6px', marginTop: '12px', flexWrap: 'wrap' }}>
                  {[1, 2, 3, 4, 5].map((p) => (
                    <span key={p} style={{ width: '28px', height: '28px', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: 700, background: p === 1 ? '#B08968' : '#f1f5f9', color: p === 1 ? '#fff' : '#64748b' }}>{p}</span>
                  ))}
                </div>
              </div>
            )}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </motion.div>

      {/* FLOATING INSIGHT (The "Hook") */}
      <motion.div 
        className="lp-hero__floating-card lp-hero__card--client"
        initial={{ opacity: 0, x: 120, y: 50 }}
        animate={{ opacity: 1, x: 0, y: 0 }}
        transition={{ duration: 1, delay: 0.8 }}
        style={{ 
          width: '260px', 
          background: 'rgba(255,255,255,0.9)', 
          backdropFilter: 'blur(12px)',
          border: '1px solid rgba(255,255,255,0.4)',
          padding: '22px',
          boxShadow: '0 25px 60px rgba(0,0,0,0.15)',
          borderRadius: '24px',
          zIndex: 100,
          right: '-150px',
          bottom: '80px'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '14px' }}>
          <div style={{ width: '38px', height: '38px', background: 'linear-gradient(135deg, #fdfaf7, #F6C674)', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#A5765C', boxShadow: '0 4px 10px rgba(246,198,116,0.3)' }}>
            <Dog size={20} />
          </div>
          <div>
            <div style={{ fontSize: '13px', fontWeight: 900, color: '#1e293b' }}>{t(`${ns}.floatingCard.petName`)}</div>
            <div style={{ fontSize: '11px', color: '#A5765C', fontWeight: 600 }}>{t(`${ns}.floatingCard.status`)}</div>
          </div>
        </div>
        <div style={{ fontSize: '11.5px', color: '#475569', lineHeight: 1.6, padding: '12px', background: 'rgba(165,118,92,0.06)', borderRadius: '12px', border: '1px solid rgba(165,118,92,0.1)' }}>
          &ldquo;{t(`${ns}.floatingCard.note`)}&rdquo;
        </div>
        <div style={{ marginTop: '12px', display: 'flex', justifyContent: 'flex-end' }}>
          <div style={{ fontSize: '10px', color: '#A5765C', fontWeight: 800 }}>{t(`${ns}.floatingCard.share`)}</div>
        </div>
      </motion.div>
    </div>
  );
};

export default AppShowcaseVisual;
